/**
 * @lodex-pro/web-portal/hooks/useHaulRequests — operator portal rework PR 12a: a pumper,
 * foreman or admin turns a load in to a hauler ("Request a haul"), and sees what they turned in
 * ("My requests").
 *
 * Reads go through `useApiQuery`; the write uses `useMutation` over `authedFetch` and throws
 * `ApiError` (status + catalog code), like useTankSelections. Unlike those writes, the error's
 * message is the API body's own `error.message` (user-facing words, e.g. the 409
 * HAUL_REQUEST_HAULER_UNAVAILABLE), or '' when the body carried none.
 *
 * Types only from @lodex-pro/shared (DEC-149 §9).
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { ApiError, authedFetch, useApiOpts, useApiQuery } from '@lodex-pro/web-shared';
import type {
  CreatePortalHaulRequest,
  HaulRequestTargetView,
  PortalHaulRequestView,
} from '@lodex-pro/shared/validation/operator-portal-routes';

const BASE = '/v1/operator-portal';

// ── Query keys ───────────────────────────────────────────────────────────────

export const HAUL_REQUEST_TARGETS_KEY: readonly string[] = [
  'operator-portal',
  'haul-request-targets',
];
export const MY_REQUESTS_KEY: readonly string[] = ['operator-portal', 'my-requests'];

// ── Reads ────────────────────────────────────────────────────────────────────

/** Every (tank, hauler) the caller can turn a load in for, with that hauler's job types. */
export function useHaulRequestTargets(): ReturnType<
  typeof useApiQuery<{ targets: HaulRequestTargetView[] }>
> {
  const opts = useApiOpts();
  return useApiQuery<{ targets: HaulRequestTargetView[] }>(opts, `${BASE}/haul-requests/targets`, {
    queryKey: HAUL_REQUEST_TARGETS_KEY,
    staleTime: 30_000,
  });
}

/** What the caller turned in, and their matched requestor's loads. */
export function useMyRequests(): ReturnType<
  typeof useApiQuery<{ requests: PortalHaulRequestView[] }>
> {
  const opts = useApiOpts();
  return useApiQuery<{ requests: PortalHaulRequestView[] }>(opts, `${BASE}/my-requests`, {
    queryKey: MY_REQUESTS_KEY,
    staleTime: 30_000,
  });
}

// ── Write ────────────────────────────────────────────────────────────────────

/** The `error.code` and `error.message` of an API error body, or nulls for any other shape. */
function bodyError(body: unknown): { code: string | null; message: string | null } {
  if (typeof body !== 'object' || body === null || !('error' in body)) {
    return { code: null, message: null };
  }
  const err: unknown = body.error;
  if (typeof err !== 'object' || err === null) return { code: null, message: null };
  const code = 'code' in err && typeof err.code === 'string' ? err.code : null;
  const message = 'message' in err && typeof err.message === 'string' ? err.message : null;
  return { code, message };
}

export interface CreateHaulRequestResult {
  readonly request: PortalHaulRequestView;
  /** true: the same clientRequestId was already turned in; this is the first request. */
  readonly replayed: boolean;
}

/** Turn a load in. The caller generates `clientRequestId` once per submit and reuses it on retry. */
export function useCreateHaulRequest(): UseMutationResult<
  CreateHaulRequestResult,
  Error,
  CreatePortalHaulRequest
> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useMutation<CreateHaulRequestResult, Error, CreatePortalHaulRequest>({
    mutationFn: async (body: CreatePortalHaulRequest): Promise<CreateHaulRequestResult> => {
      const res = await authedFetch(opts, `${BASE}/haul-requests`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        let parsed: { code: string | null; message: string | null } = {
          code: null,
          message: null,
        };
        try {
          parsed = bodyError(await res.json());
        } catch {
          // Not JSON (a proxy or gateway error): no code, no words.
        }
        throw new ApiError(res.status, parsed.code, parsed.message ?? '', [], parsed.message);
      }
      return (await res.json()) as CreateHaulRequestResult;
    },
    onSettled: (): void => {
      void qc.invalidateQueries({ queryKey: MY_REQUESTS_KEY });
    },
  });
}
